import { gql } from '@/core/shared/api';
import { fullLocation } from './fragments';
import AudioService from '@/core/AudioService';
import moment from 'moment-timezone';
import { isEmpty } from 'lodash-es';
import deepmerge from 'deepmerge';

import refreshWorker from '@/store/timer/refresh.worker';

const refresh = refreshWorker();

refresh.onmessage = e => {
  if (e.data.message === 'timeEnded') AudioService.timeEnded();
};

export const getLocations = async ({ commit, rootGetters, dispatch }) => {
  commit('setLoading', true);
  commit('locations/set', {
    path: 'isFetchingLocations',
    value: true,
  });

  const query = `{
    locations {
      ${fullLocation}
    }
  }`;

  try {
    const { locations } = await gql.request(query);

    dispatch('insertEntities', {
      name: 'Location',
      payload: locations,
    });

    const locationIdsFromApi = locations.map(location => location.id);
    rootGetters['entities/Location/all']().forEach(location => {
      if (!locationIdsFromApi.includes(location.id)) {
        dispatch('removeEntities', {
          name: 'Location',
          payload: location,
        });
      }
    });

    return locations;
  } finally {
    commit('setLoading', false);
    commit('locations/set', {
      path: 'isFetchingLocations',
      value: false,
    });
  }
};

export const refreshLocation = async ({ dispatch, rootGetters }, locationId) => {
  const bookings = rootGetters.locationBookings(locationId).map(booking => deepmerge({}, booking));

  const todayBookings = await refresh.getTodayBookings(bookings, rootGetters.currentTime);

  let stationsWithBookingsPlaying = [];

  await Promise.all(todayBookings.map(async booking => {
    await Promise.all(booking.bookingStationTimes.map(async bookingStationTime => {
      const bookingStationTimeChanges = {};

      const { playing, endTime, newBookingStationTime } = await refresh.refreshBookingStationTimePlayingAndEndTime(
        bookingStationTime,
        rootGetters.currentTime,
      );

      if (bookingStationTime._playing !== playing) {
        bookingStationTimeChanges._playing = playing;
      }
      if (endTime && bookingStationTime.endTime !== endTime) {
        bookingStationTimeChanges.endTime = endTime;
      }
      if (newBookingStationTime && bookingStationTime._new !== newBookingStationTime) {
        bookingStationTimeChanges._new = newBookingStationTime;
      }

      const accurateEndTime = bookingStationTimeChanges.endTime || bookingStationTime.endTime;

      const stationInStore = rootGetters['entities/Station'](bookingStationTime.station.id);
      const stationChanges = {};

      if (playing) {
        stationsWithBookingsPlaying = [...stationsWithBookingsPlaying, bookingStationTime.station.id];

        if (stationInStore._pausedAt !== bookingStationTime.pausedAt) {
          stationChanges._pausedAt = bookingStationTime.pausedAt;
          stationChanges._pausedDuration = bookingStationTime.pausedDuration;
        }

        if (stationInStore._endTime !== accurateEndTime) {
          stationChanges._endTime = accurateEndTime;
        }
      }

      if (!isEmpty(stationChanges)) {
        dispatch('insertEntities', {
          name: 'Station',
          payload: { id: bookingStationTime.station.id, ...stationChanges },
        });
      }

      if (!isEmpty(bookingStationTimeChanges)) {
        dispatch('insertEntities', {
          name: 'BookingStationTime',
          payload: { id: bookingStationTime.id, ...bookingStationTimeChanges },
        });
      }
    }));
  }));

  // any stations that were playing previously, but had their booking deleted make their time null!
  rootGetters.locationStations(locationId).forEach(station => {
    if (!stationsWithBookingsPlaying.includes(station.id) && station._endTime) {
      dispatch('insertEntities', {
        name: 'Station',
        payload: { id: station.id, _endTime: null },
      });
    }
  });
};

export const refreshEntireLocationFromApi = ({ dispatch, rootGetters }, locationId) => {
  dispatch('getLocationExperiences', locationId);
  dispatch('getLocationStations', locationId);
  dispatch('getLocationBookings', {
    locationId,
    startTime: rootGetters.calendarDate,
    endTime: moment(rootGetters.calendarDate).add(1, 'day').format(),
  });
  dispatch('getContent');
  dispatch('getStationClosings', {
    locationId,
    startTime: rootGetters.calendarDate,
    endTime: moment(rootGetters.calendarDate).add(1, 'day').format(),
  });
};

export const storeLocation = async ({ commit, dispatch, rootState }, location) => {
  const mutation = `mutation storeLocation ($location: LocationInput) {
    storeLocation (location: $location) {
      id,
      darkMode
    }
  }`;

  try {
    await gql.request(mutation, {
      location: {
        id: location.id,
        darkMode: !location.darkMode,
      },
    });

    window.location.reload();
  } catch (e) {
  }
};
